import { computed, ref, shallowRef } from 'vue';

import { defineStore } from 'pinia';

import { config } from '@layouts/config';
import { isNavLinkActive } from '@layouts/utils';

import {
  collectGroupKeys,
  computeCurrentPathKeys,
  parseStoredState,
  pruneOpenKeys,
  resolveCollapseAll,
  resolveExpandAll,
  resolveInitialOpen,
  resolveRouteChangeOpen,
  resolveToggleOpen,
  serializeState,
} from '@/helpers/navGroups';

const storageKey = () => `${config.app.title}-navGroups`;

const readStored = () => {
  try {
    return parseStoredState(localStorage.getItem(storageKey()));
  } catch {
    return null;
  }
};

export const useNavGroupsStore = defineStore('navGroups', () => {
  const stored = readStored();

  const open = ref(stored ? stored.open : []);
  const auto = ref(stored ? stored.auto : false);
  const currentPathKeys = shallowRef(new Set());

  const hadStoredState = !!stored;
  let initialized = false;
  let allKeys = [];

  const openSet = computed(() => new Set(open.value));

  const persist = () => {
    try {
      localStorage.setItem(storageKey(), serializeState({ open: open.value, auto: auto.value }));
    } catch {
      // Blocked or full storage: the state stays in memory only.
    }
  };

  const setOpen = (next) => {
    open.value = next;
    persist();
  };

  const isOpen = (key) => openSet.value.has(key);

  // Called from a component (the router cannot be resolved inside a store) on the first render
  // and on every route.path change.
  const syncRoute = (tree, router) => {
    allKeys = collectGroupKeys(tree);
    currentPathKeys.value = computeCurrentPathKeys(tree, (link) => isNavLinkActive(link, router));

    if (!initialized) {
      initialized = true;
      const prunedStored = hadStoredState
        ? { open: pruneOpenKeys(open.value, allKeys), auto: auto.value }
        : null;
      setOpen(resolveInitialOpen({ stored: prunedStored, currentPathKeys: currentPathKeys.value }));
      return;
    }

    setOpen(
      resolveRouteChangeOpen({
        open: open.value,
        auto: auto.value,
        currentPathKeys: currentPathKeys.value,
      }),
    );
  };

  const toggle = (key) => {
    setOpen(
      resolveToggleOpen({
        open: open.value,
        key,
        auto: auto.value,
        currentPathKeys: currentPathKeys.value,
        allKeys,
      }),
    );
  };

  const collapseAll = () => {
    setOpen(resolveCollapseAll({ currentPathKeys: currentPathKeys.value }));
  };

  const expandAll = () => {
    setOpen(resolveExpandAll({ allKeys }));
  };

  const setAuto = (value) => {
    auto.value = !!value;
    if (auto.value) {
      collapseAll();
      return;
    }
    persist();
  };

  return {
    open,
    auto,
    currentPathKeys,
    isOpen,
    syncRoute,
    toggle,
    collapseAll,
    expandAll,
    setAuto,
  };
});
